export function buildProductKey(manufacturerName: string | null | undefined, model: string) {
  const normalizedModel = model.trim().replace(/\s+/g, ' ').toUpperCase()
  const normalizedManufacturer = manufacturerName?.trim().replace(/\s+/g, ' ').toUpperCase()
  return normalizedManufacturer ? `${normalizedManufacturer}:${normalizedModel}` : normalizedModel
}

export type CatalogProductIdentity = {
  productid: string
  statecode: number
  statuscode: number
}

export type ProductCreateTarget = {
  productId: string
  source: 'selected' | 'catalog' | 'created'
}

export function isCatalogProductEligible(product: CatalogProductIdentity) {
  return product.statecode === 0 && product.statuscode === 1
}

export async function resolveProductCreateTarget({
  selectedProductId,
  expectedProductKey,
  productKey,
  findProductByNumber,
  createProduct,
}: {
  selectedProductId?: string
  expectedProductKey?: string
  productKey: string
  findProductByNumber: (key: string) => Promise<CatalogProductIdentity | null>
  createProduct: () => Promise<string>
}): Promise<ProductCreateTarget> {
  if (selectedProductId && !expectedProductKey) {
    return { productId: selectedProductId, source: 'selected' }
  }

  const lookupKey = expectedProductKey ?? productKey
  const exactProduct = await findProductByNumber(lookupKey)

  if (expectedProductKey && !exactProduct) {
    throw new Error('Catalog product changed before it could be added. Check the model and try again.')
  }

  if (exactProduct && !isCatalogProductEligible(exactProduct)) {
    throw new Error('This catalog product is inactive or unavailable and cannot be added or recreated.')
  }

  if (exactProduct) {
    return { productId: exactProduct.productid, source: 'catalog' }
  }

  return { productId: await createProduct(), source: 'created' }
}

export type ExistingProductPriceValues = {
  description: string | null
  currentcost: number | null
  price: number | null
  msdyn_taxable: boolean | null
  dsy_installationlabor: number | null
  dsy_programminglabor: number | null
  dsy_roughinlabor: number | null
  dsy_engineeringlabor: number | null
  dsy_projectmanagementlabor: number | null
}

export type ExistingProductPriceListItemValues = {
  productId: string
  priceListId: string
  description?: string
  unitCost: number
  unitPrice: number
  taxable: boolean
  installationLabor: number
  programmingLabor: number
  roughInLabor: number
  engineeringLabor: number
  projectManagementLabor: number
}

export async function ensureExistingProductPriceListItem<T>({
  productId,
  priceListId,
  findPriceListItem,
  getProduct,
  createPriceListItem,
  getPriceListItem,
}: {
  productId: string
  priceListId: string
  findPriceListItem: () => Promise<T | null>
  getProduct: () => Promise<ExistingProductPriceValues>
  createPriceListItem: (values: ExistingProductPriceListItemValues) => Promise<string>
  getPriceListItem: (id: string) => Promise<T>
}): Promise<T> {
  const existingPriceListItem = await findPriceListItem()
  if (existingPriceListItem) return existingPriceListItem

  const product = await getProduct()
  const priceListItemId = await createPriceListItem({
    productId,
    priceListId,
    description: product.description || undefined,
    unitCost: product.currentcost ?? 0,
    unitPrice: product.price ?? 0,
    taxable: product.msdyn_taxable ?? true,
    installationLabor: product.dsy_installationlabor ?? 0,
    programmingLabor: product.dsy_programminglabor ?? 0,
    roughInLabor: product.dsy_roughinlabor ?? 0,
    engineeringLabor: product.dsy_engineeringlabor ?? 0,
    projectManagementLabor: product.dsy_projectmanagementlabor ?? 0,
  })

  return await getPriceListItem(priceListItemId)
}

type ManufacturerNameRecord = {
  dsy_manufacturerid: string
  dsy_manufacturername: string
}

function normalizeManufacturerName(value: string) {
  return value.trim().replace(/\s+/g, ' ').toUpperCase()
}

function canonicalizeManufacturerName(value: string) {
  return value
    .trim()
    .toUpperCase()
    .split(/[^A-Z0-9]+/)
    .filter((token) => token.length > 0)
    .map((token) => (token === 'SOFTWARE' ? 'SOFT' : token))
    .join('')
}

function tokenizeManufacturerName(value: string) {
  return normalizeManufacturerName(value)
    .split(/[^A-Z0-9]+/)
    .filter((token) => token.length > 0)
}

function editDistance(left: string, right: string) {
  const previous = Array.from({ length: right.length + 1 }, (_, index) => index)
  const current = Array.from({ length: right.length + 1 }, () => 0)

  for (let leftIndex = 1; leftIndex <= left.length; leftIndex += 1) {
    current[0] = leftIndex
    for (let rightIndex = 1; rightIndex <= right.length; rightIndex += 1) {
      const cost = left[leftIndex - 1] === right[rightIndex - 1] ? 0 : 1
      current[rightIndex] = Math.min(
        current[rightIndex - 1] + 1,
        previous[rightIndex] + 1,
        previous[rightIndex - 1] + cost
      )
    }
    previous.splice(0, previous.length, ...current)
  }

  return previous[right.length]
}

function scoreManufacturerCandidate(importedName: string, candidateName: string) {
  const normalizedImported = normalizeManufacturerName(importedName)
  const normalizedCandidate = normalizeManufacturerName(candidateName)
  if (!normalizedImported || !normalizedCandidate) return 0
  if (normalizedCandidate === normalizedImported) return 100
  if (canonicalizeManufacturerName(candidateName) === canonicalizeManufacturerName(importedName)) return 100

  const importedTokens = tokenizeManufacturerName(importedName)
  const candidateTokens = tokenizeManufacturerName(candidateName)
  if (importedTokens.length === 0 || candidateTokens.length === 0) return 0

  let score = normalizedCandidate.includes(normalizedImported) ? 30 : 0

  for (const importedToken of importedTokens) {
    const bestTokenScore = candidateTokens.reduce((bestScore, candidateToken) => {
      if (candidateToken === importedToken) return Math.max(bestScore, 24)
      if (candidateToken.startsWith(importedToken)) return Math.max(bestScore, 18)
      if (importedToken.length >= 4 && importedToken.startsWith(candidateToken)) return Math.max(bestScore, 14)
      if (candidateToken.includes(importedToken) || importedToken.includes(candidateToken)) return Math.max(bestScore, 10)

      const maxLength = Math.max(importedToken.length, candidateToken.length)
      if (maxLength >= 5 && editDistance(importedToken, candidateToken) / maxLength <= 0.25) {
        return Math.max(bestScore, 8)
      }

      return bestScore
    }, 0)

    score += bestTokenScore
  }

  return score
}

export function searchManufacturerCandidates<T extends ManufacturerNameRecord>(importedName: string, manufacturers: T[]) {
  return manufacturers
    .map((manufacturer) => ({
      manufacturer,
      score: scoreManufacturerCandidate(importedName, manufacturer.dsy_manufacturername),
    }))
    .filter(({ score }) => score > 0)
    .sort((left, right) => right.score - left.score)
    .slice(0, 5)
    .map(({ manufacturer }) => manufacturer)
}

export type ManufacturerMatchResult = {
  matchedManufacturerId: string | null
  reason?: string | null
}

export async function resolveImportedManufacturer<T extends ManufacturerNameRecord>({
  importedName,
  manufacturers,
  matchManufacturer,
}: {
  importedName: string
  manufacturers: T[] | null | undefined
  matchManufacturer: (params: {
    name: string
    candidates: { id: string; name: string }[]
  }) => Promise<ManufacturerMatchResult>
}) {
  const normalizedImported = normalizeManufacturerName(importedName)
  const canonicalImported = canonicalizeManufacturerName(importedName)
  const exactMatch = manufacturers?.find(
    (manufacturer) =>
      normalizeManufacturerName(manufacturer.dsy_manufacturername) === normalizedImported ||
      (canonicalImported && canonicalizeManufacturerName(manufacturer.dsy_manufacturername) === canonicalImported)
  )
  if (exactMatch) return exactMatch

  const candidates = searchManufacturerCandidates(importedName, manufacturers ?? [])
  if (candidates.length === 0) return null

  const match = await matchManufacturer({
    name: importedName,
    candidates: candidates.slice(0, 5).map((candidate) => ({
      id: candidate.dsy_manufacturerid,
      name: candidate.dsy_manufacturername,
    })),
  })

  return candidates.find((candidate) => candidate.dsy_manufacturerid === match.matchedManufacturerId) ?? null
}

export function getSelectedImageName(file: File | null | undefined) {
  return file?.name ?? 'No file selected'
}

/** Formats Dataverse stores directly. WebP is accepted but converted to PNG first. */
export const PRODUCT_IMAGE_MIME_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/tiff', 'image/bmp']
export const PRODUCT_IMAGE_UPLOAD_MIME_TYPES = [...PRODUCT_IMAGE_MIME_TYPES, 'image/webp']
export const PRODUCT_IMAGE_ACCEPT = '.jpg,.jpeg,.png,.gif,.tif,.tiff,.bmp,.webp'
export const PRODUCT_IMAGE_MAX_KB = 5120

export type PreparedProductImage = { file: File } | { error: { title: string; description: string } }

/**
 * Validate a picked catalog image and hand back something Dataverse accepts —
 * WebP is re-encoded to PNG because entityimage rejects it. Shared by every
 * dialog that uploads a product image so the rules stay in one place.
 */
export async function prepareProductImageFile(file: File): Promise<PreparedProductImage> {
  if (!PRODUCT_IMAGE_UPLOAD_MIME_TYPES.includes(file.type)) {
    return {
      error: {
        title: 'Unsupported file format',
        description: 'Please upload a JPG, PNG, GIF, TIFF, BMP, or WebP image.',
      },
    }
  }
  if (file.size > PRODUCT_IMAGE_MAX_KB * 1024) {
    return { error: { title: 'File too large', description: 'Product image must be under 5 MB.' } }
  }
  if (file.type !== 'image/webp') return { file }

  const pngFile = await new Promise<File | null>((resolve) => {
    const img = new Image()
    img.onload = () => {
      const canvas = document.createElement('canvas')
      canvas.width = img.width
      canvas.height = img.height
      const ctx = canvas.getContext('2d')
      if (!ctx) {
        URL.revokeObjectURL(img.src)
        resolve(null)
        return
      }
      ctx.drawImage(img, 0, 0)
      canvas.toBlob(
        (blob) => {
          URL.revokeObjectURL(img.src)
          if (!blob) return resolve(null)
          resolve(new File([blob], file.name.replace(/\.webp$/i, '.png'), { type: 'image/png' }))
        },
        'image/png',
        1
      )
    }
    img.onerror = () => {
      URL.revokeObjectURL(img.src)
      resolve(null)
    }
    img.src = URL.createObjectURL(file)
  })

  if (!pngFile) {
    return { error: { title: 'Could not read image', description: 'Convert the image to PNG and try again.' } }
  }
  return { file: pngFile }
}

export function isTemporaryQuoteDetailId(id: string | null | undefined) {
  return id?.startsWith('temp-') ?? false
}

export function replaceTemporaryQuoteDetailInCache<T extends { quotedetailid: string }>(
  cache: T[] | undefined,
  tempId: string,
  replacement: T
) {
  if (!cache) return cache
  let replaced = false
  const next = cache.map((detail) => {
    if (detail.quotedetailid !== tempId) return detail
    replaced = true
    return replacement
  })
  return replaced ? next : cache
}
